import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TicketCategoryEnum, TicketEvents, TicketSeverityEnum, TicketStatusEnum } from "../../types";
import { SessionService } from "../../../../session.service";
import { TicketRepositoryInterface } from "../../repository/TicketRepositoryInterface";
import { Ticket } from "../../model/Ticket";
import { EventDispatcherService } from "../../../../shared/services/event-dispatcher/event-dispatcher.service";
import * as uuid from "uuid";

@Component({
  selector: 'app-create-ticket-modal',
  templateUrl: './create-ticket-modal.component.html',
  styleUrls: ['./create-ticket-modal.component.scss']
})
export class CreateTicketModalComponent implements OnInit {
  protected readonly TicketCategoryEnum = TicketCategoryEnum;

  supportForm: FormGroup;
  // Drives the category tile grid (Stripe/Google-style visual picker,
  // replacing the old mat-select) — one entry per TicketCategoryEnum value.
  categoryOptions: { value: TicketCategoryEnum; label: string; icon: string }[] = [
    { value: TicketCategoryEnum.UI_BUG, label: 'UI Bug', icon: 'bug_report' },
    { value: TicketCategoryEnum.BILLING_ISSUES, label: 'Billing Issues', icon: 'credit_card' },
    { value: TicketCategoryEnum.COMPLIANCE_QUESTION, label: 'Compliance Question', icon: 'gavel' },
    { value: TicketCategoryEnum.FEATURE_REQUEST, label: 'Feature Request', icon: 'lightbulb' },
    { value: TicketCategoryEnum.OTHER, label: 'Other', icon: 'help_outline' },
  ];
  urgencyLevels = [
    { value: TicketSeverityEnum.LOW, matIcon: 'arrow_downward ' },
    { value: TicketSeverityEnum.MEDIUM, matIcon: 'warning' },
    { value: TicketSeverityEnum.HIGH, matIcon: 'priority_high' }
  ];
  acceptedFileTypes = '.png,.jpg,.pdf,.txt,.docx';
  maxFileSize = 25 * 1024 * 1024; // 25MB
  maxFiles = 5;
  uploadedFiles: File[] = [];
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateTicketModalComponent>,
    private snackBar: MatSnackBar,
    private sessionService: SessionService,
    private eventDispatcher: EventDispatcherService,
    @Inject('TicketRepositoryInterface') private ticketRepository: TicketRepositoryInterface,
  ) {
    this.supportForm = this.fb.group({
      subject: ['', [Validators.maxLength(100)]],
      category: ['', Validators.required],
      urgency: [TicketSeverityEnum.LOW, Validators.required],
      description: ['', [Validators.required, Validators.minLength(20), Validators.maxLength(2000)]],
    }, { validators: this.subjectRequiredValidator() });
  }

  ngOnInit(): void {
    this.supportForm.get('category')?.valueChanges
      .subscribe((value) => {
        value && this.supportForm.get('subject')?.reset(null);
      });
  }

  private subjectRequiredValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const category = formGroup.get('category')?.value;
      const subject = formGroup.get('subject')?.value;

      if (category === TicketCategoryEnum.OTHER && !subject) {
        formGroup.get('subject')?.setErrors({ required: true });
        return { subjectRequired: true };
      } else {
        formGroup.get('subject')?.setErrors(null);
        return null;
      }
    };
  }

  isDraggingFiles = false;

  onFileSelected(event: any): void {
    this.addFiles(event.target.files as FileList);
    event.target.value = '';
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingFiles = false;
    if (event.dataTransfer?.files) this.addFiles(event.dataTransfer.files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingFiles = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingFiles = false;
  }

  private addFiles(files: FileList | null): void {
    if (!files || files.length === 0) return;

    // Check total files won't exceed max
    if (this.uploadedFiles.length + files.length > this.maxFiles) {
      this.snackBar.open(`Maximum ${this.maxFiles} files allowed`, 'OK', { duration: 5000 });
      return;
    }

    // Check each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!['png', 'jpg', 'pdf', 'txt', 'docx'].includes(fileExt || '')) {
        this.snackBar.open(`Invalid file type: ${file.name}`, 'OK', { duration: 5000 });
        continue;
      }

      // Check file size
      if (file.size > this.maxFileSize) {
        this.snackBar.open(`File too large: ${file.name} (max 25MB)`, 'OK', { duration: 5000 });
        continue;
      }

      this.uploadedFiles.push(file);
    }
  }

  selectCategory(value: TicketCategoryEnum): void {
    const control = this.supportForm.get('category');
    control?.setValue(value);
    control?.markAsDirty();
    control?.markAsTouched();
  }

  setUrgency(value: TicketSeverityEnum): void {
    this.supportForm.get('urgency')?.setValue(value);
    this.supportForm.get('urgency')?.markAsDirty();
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  onSubmit(): void {
    if (this.supportForm.invalid) {
      this.supportForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    const ticket = new Ticket(
      uuid.v4(),
      this.supportForm.get('category')?.value as TicketCategoryEnum,
      this.supportForm.get('subject')?.value || '',
      this.supportForm.get('description')?.value || '',
      new Date(),
      TicketStatusEnum.OPEN,
      this.sessionService.getValue('userId'),
      this.sessionService.getValue('name'),
      this.sessionService.getValue('email'),
      this.sessionService.getValue('clientId'),
      'a business name',
      this.supportForm.get('urgency')?.value || TicketSeverityEnum.LOW,
      '',
      '',
      '',
      this.uploadedFiles
    );

    this.ticketRepository.create(ticket).subscribe(() => {
      this.isLoading = false;
      this.snackBar.open(
        'Your request has been submitted. A confirmation email has been sent.',
        'OK',
        { duration: 5000 }
      );
      this.dialogRef.close(true);

      this.eventDispatcher.dispatchEvent(TicketEvents.CREATED, ticket);
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
